import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CertificationCompletionsSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function CertificationCompletionsSubTab() {
  const [completions, setCompletions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCompletion, setEditingCompletion] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    telegram_id: '',
    certification_topic_id: '',
    completed_at: ''
  });

  useEffect(() => {
    fetchCompletions();
    fetchTopics();
  }, []);

  const fetchCompletions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/certification-completions`);
      setCompletions(response.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке статистики: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/certification-topics`);
      setTopics(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке тем сертификаций:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    return d.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleEdit = (completion) => {
    setEditingCompletion(completion);
    setShowCreateForm(false);
    const completedAt = completion.completed_at
      ? new Date(completion.completed_at).toISOString().slice(0, 16)
      : '';
    setFormData({
      telegram_id: completion.telegram_id.toString(),
      certification_topic_id: completion.certification_topic_id.toString(),
      completed_at: completedAt
    });
  };

  const handleCreate = () => {
    setEditingCompletion(null);
    setShowCreateForm(true);
    setFormData({
      telegram_id: '',
      certification_topic_id: topics.length > 0 ? topics[0].id.toString() : '',
      completed_at: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        telegram_id: parseInt(formData.telegram_id) || 0,
        certification_topic_id: parseInt(formData.certification_topic_id) || 0
      };
      if (formData.completed_at && formData.completed_at.trim()) {
        payload.completed_at = new Date(formData.completed_at).toISOString();
      }
      if (editingCompletion) {
        await axios.put(`${API_URL}/api/certification-completions/${editingCompletion.id}`, payload);
        setEditingCompletion(null);
      } else {
        await axios.post(`${API_URL}/api/certification-completions`, payload);
        setShowCreateForm(false);
      }
      setFormData({ telegram_id: '', certification_topic_id: '', completed_at: '' });
      fetchCompletions();
    } catch (err) {
      setError('Ошибка при сохранении: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleCancel = () => {
    setEditingCompletion(null);
    setShowCreateForm(false);
    setFormData({ telegram_id: '', certification_topic_id: '', completed_at: '' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эту запись?')) {
      try {
        await axios.delete(`${API_URL}/api/certification-completions/${id}`);
        fetchCompletions();
      } catch (err) {
        setError('Ошибка при удалении: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="certification-completions-sub-tab">
      <div className="tab-header">
        <h3>Статистика прохождений сертификаций</h3>
        <button type="button" className="btn btn-primary" onClick={handleCreate}>
          Добавить запись
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {(editingCompletion || showCreateForm) && (
        <div className="form-container">
          <h4>{editingCompletion ? 'Редактировать запись' : 'Новая запись о прохождении'}</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="telegram_id">Telegram ID пользователя *</label>
              <input
                type="number"
                id="telegram_id"
                value={formData.telegram_id}
                onChange={(e) => setFormData({ ...formData, telegram_id: e.target.value })}
                required
                min="1"
                placeholder="123456789"
              />
            </div>
            <div className="form-group">
              <label htmlFor="certification_topic_id">Тема сертификации *</label>
              <select
                id="certification_topic_id"
                value={formData.certification_topic_id}
                onChange={(e) => setFormData({ ...formData, certification_topic_id: e.target.value })}
                required
              >
                <option value="">— Выберите тему —</option>
                {topics.map((t) => (
                  <option key={t.id} value={t.id}>{t.title}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="completed_at">Дата прохождения</label>
              <input
                type="datetime-local"
                id="completed_at"
                value={formData.completed_at}
                onChange={(e) => setFormData({ ...formData, completed_at: e.target.value })}
              />
              <span className="form-hint">Оставьте пустым для текущей даты</span>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Сохранить</button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>Отмена</button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Тема сертификации</th>
              <th>Telegram ID</th>
              <th>Дата прохождения</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {completions.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-state">
                  Нет записей о прохождении сертификаций.
                </td>
              </tr>
            ) : (
              completions.map((comp) => (
                <tr key={comp.id}>
                  <td>{comp.id}</td>
                  <td>{comp.certification_topic_title || 'N/A'}</td>
                  <td>{comp.telegram_id}</td>
                  <td>{formatDate(comp.completed_at)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(comp)}
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(comp.id)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CertificationCompletionsSubTab;
